import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {EChartsOption} from 'echarts';
import {Constants} from 'src/app/shared/utils/constants';
import {getDates, getMonths} from 'src/app/shared/utils/helper';
import {forkJoin, Subscription} from 'rxjs';
import {DashboardService} from '../dashboard.service';
import {Profit} from 'src/app/models/fund-performance.model';

import {getNewEnd, getNewStart} from 'src/app/shared/utils/chart-helper';
import {DatePipe, DecimalPipe} from '@angular/common';
import {Broadcaster} from 'src/app/app.broadcast';

interface Line {
  type: string;
  text: string;
  value: string;
  selected: boolean;
  width: number;
  height: number;
  color: string;
}

export interface ProfitData {
  [key: string] : Profit[]
}
@Component({
  selector: 'fx-profit',
  templateUrl: './profit.component.html',
  styleUrls: ['./profit.component.sass'],
})
export class ProfitComponent implements OnInit, OnDestroy {
  @Input()
  set channels(val) {
    this.channel = val;
    this.lines = Constants[this.channel]
      .filter(f => !['total', 'wallet'].includes(f.value))
      .map((ex: any) => {
        return {
          type: 'circle',
          text: ex.name,
          value: ex.value,
          selected: ex.selected,
          width: 18,
          height: 18,
          color: ex.color,
        };
    });
  }
  @Input() currency;
  @Input()
  set dataProfit(val) {
    if (Object.keys(val).length !== 0) {
      this.data = val;
      this.initChart();
    }
  }
  @Output() emitHistory = new EventEmitter();
  lines: Line[] = [];
  isLoadingMoreData = false;
  chartOption: EChartsOption;
  updateChartOption: EChartsOption;
  xAxisCategories: string[];
  months = Constants.MONTHS;
  data: ProfitData = {};
  subscription: Subscription;
  timeInfo = {
    tf: 'day',
    currentStart: undefined,
    currentEnd: undefined,
    min: undefined
  };
  channel = 'EXCHANGES';
  updateTime: Date;
  formatNumber = Constants.FIXED_NUMBER;
  symbol = Constants.SYMBOL_CURRENCY;
  constructor(
    private service: DashboardService,
    private date: DatePipe,
    private broadcaseter: Broadcaster,
    private fxNumber: DecimalPipe
  ) {}

  ngOnInit(): void {}

  onSelectExchange(): void {
    this.getExchanges();
  }

  getExchanges(): void {
    this.initChart();
  }
  getXAxisCategories(data?: Profit[]): string[] {
    if (data) {
      return data.map(d => d.time_);
    } else {
      if (this.timeInfo.tf === 'hour') {
        return getMonths(this.months, 12, true);
      } else if (this.timeInfo.tf === 'day') {
        return getDates(32, true).map(d => d.toString());
      } else if (this.timeInfo.tf === 'week') {
        return getDates(10, true).map(d => d.toString());
      }
    }
  }

  initChart(): void {
    const series = [];
    this.chartOption = {};
    let gotXAxis = false;
    setTimeout(() => {
      this.xAxisCategories = this.getXAxisCategories();
      this.lines.forEach((ex: any, i: number) => {
        if (ex.selected) {
          if (!gotXAxis) {
            gotXAxis = true;
            this.xAxisCategories = this.getXAxisCategories(this.data[ex.value]);
            series.push({
              data: this.getChartData(this.data[ex.value]),
              type: 'line',
              showSymbol: false,
              symbolSize: 1,
              itemStyle: { color: ex.color },
              lineStyle: {
                color: ex.color,
                width: 1,
              },
              markLine: {
                silent: true,
                symbol: false,
                lineStyle: {
                  color: '#727285',
                  type: 'dashed',
                },
                data: [{ yAxis: 0, label: { show: false } }],
              },
            });
          } else {
            series.push({
              data: this.getChartData(this.data[ex.value]),
              type: 'line',
              showSymbol: false,
              symbolSize: 1,
              itemStyle: { color: ex.color },
              lineStyle: {
                color: ex.color,
                width: 1,
              },
            });
          }
        }
      });
      this.chartOption = {
        textStyle: {
          fontFamily: 'Roboto Condensed',
        },
        title: {
          text: this.currency.toUpperCase(),
          textStyle: { color: '#727285', fontStyle: 'normal', fontSize: 13 },
        },
        tooltip: {
          trigger: 'axis',
          formatter: (res) => {
            let labelShow  = '';
            res.map((v, index) => {
              const currencyWithSymbol = `${this.symbol[this.currency]} ${this.fxNumber.transform(+(res[index].value),this.formatNumber[this.currency])}`;
              labelShow  = `<br/>${currencyWithSymbol}`;
            });
            return labelShow;
          },
        },
        grid: {
          top: 30,
          left: 0,
          right: 10,
          bottom: 0,
          show: false,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: this.xAxisCategories,
          boundaryGap: false,
          splitLine: { show: false },
          axisTick: {
            show: false,
          },
          axisLabel: {
            rotate: 50,
          },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: '#727285' },
          },
        },
        yAxis: {
          type: 'value',
          splitLine: { show: false },
          axisLabel: {
            showMinLabel: false,
            showMaxLabel: false,
          },
          axisTick: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: { color: '#727285' },
          },
        },
        dataZoom: [
          {
            id: 'profitDataZoom',
            type: 'inside',
            xAxisIndex: 0,
            start: 50,
            end: 100,
            zoomLock: true,
          },
        ],
        series,
      };
    });
  }

  zoom(event: any): void {
    this.timeInfo.currentStart = event.batch[0].start;
    this.timeInfo.currentEnd = event.batch[0].end;
    if (event.batch[0].start < 10 && !this.isLoadingMoreData) {
      // this.loadMoreData();
    }
  }

  onChartClick(event: any): void {
    this.broadcaseter.broadcast('UPDATED_PROFIT_TIMESTAMP', event.data.time);
  }

  async loadMoreData(): Promise<any> {
    this.isLoadingMoreData = true;
    const subs = [];
    this.lines.forEach((ex: any) => {
      if (ex.selected) {
        const params = {
          offset: this.getOffset(),
          timestamp: 0,
          timeframe: this.timeInfo.tf,
        };
        subs.push(this.service.getDashboardHistory(ex.value, params));
      }
    });

    this.subscription = forkJoin(subs).subscribe(async (data: any) => {
      data.forEach((item: any, i: number) => {
        if (item.status === Constants.RESPONSE_STATUS.SUCCESS) {
          this.data[this.lines[i].value] = item.data.concat(
            this.data[this.lines[i].value]
          );
          this.timeInfo.min = item.metadata.startPoint;
        }
      });
      await this.updateChart(true, this.getOffset());
      this.isLoadingMoreData = false;
    });
  }

  async updateChart(
    isLoadedMoreData: boolean,
    changingPointNumber: number = 0
  ): Promise<any> {
    const series = [];
    let newLen = 0;
    this.lines.forEach((ex: any, i: number) => {
      if (i === 0) {
        this.xAxisCategories = this.getXAxisCategories(this.data[ex.value]);
        series.push({
          data: this.getChartData(this.data[ex.value]),
          type: 'line',
          showSymbol: false,
          symbolSize: 1,
          lineStyle: {
            color: ex.color,
            width: 1,
          },
          markLine: {
            silent: true,
            symbol: false,
            lineStyle: {
              color: '#727285',
              type: 'dashed',
            },
            data: [{ yAxis: 0, label: { show: false } }],
          },
        });
      } else {
        series.push({
          data: this.getChartData(this.data[ex.value]),
          type: 'line',
          showSymbol: false,
          symbolSize: 1,
          lineStyle: {
            color: ex.color,
            width: 1,
          },
        });
      }
      if (newLen < this.data[ex.value].length) {
        newLen = this.data[ex.value].length;
      }
    });
    if (isLoadedMoreData) {
      const newStart = getNewStart(
        newLen,
        this.timeInfo.currentStart,
        changingPointNumber
      );
      const newEnd = getNewEnd(
        newLen,
        this.timeInfo.currentEnd,
        changingPointNumber
      );
      this.updateChartOption = {
        xAxis: {
          data: this.xAxisCategories,
        },
        dataZoom: [
          {
            start: newStart,
            end: newEnd,
          },
        ],
        series,
      };
    } else {
      this.updateChartOption = {
        series,
      };
    }
  }

  getChartData(data: Profit[]): any {
    return data
      ? data.map((item: Profit) => {
          return {
            value: item.value,
            time: item.time_, 
          };
        })
      : [];
  }

  getOffset(): any {
    switch (this.timeInfo.tf) {
      case 'hour':
        return 24;
      case 'day':
        return 24;
      case 'week':
        return 10;
    }
  }

  private formatCurrency(curren, value): string {
    const formatter = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: curren
    });
    return formatter.format(value);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
